<script setup>
import { useI18n } from 'vue-i18n';

import ConfigForm from '../components/ConfigForm.vue';
import IntentSetPicker from '../components/IntentSetPicker.vue';
import StudioSection from '../components/StudioSection.vue';

defineProps({
  bot: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const modeOptions = [
  { value: 'shadow', label: t('AI_AGENT.INTENT.FIELDS.MODE_OPTIONS.SHADOW') },
  { value: 'live', label: t('AI_AGENT.INTENT.FIELDS.MODE_OPTIONS.LIVE') },
];

const SECTION_ICONS = {
  GENERAL: 'i-lucide-brain',
  ESCALATION: 'i-lucide-siren',
  UNDERSTANDING: 'i-lucide-languages',
  PERFORMANCE: 'i-lucide-gauge',
};

const sections = Object.entries(SECTION_ICONS).map(([key, icon]) => ({
  key,
  icon,
  title: t(`AI_AGENT.INTENT.SECTIONS.${key}`),
  description: t(`AI_AGENT.INTENT.SECTIONS.${key}_HELP`),
}));

const fields = [
  {
    path: 'intent.enabled',
    section: 'GENERAL',
    label: 'AI_AGENT.INTENT.FIELDS.ENABLED',
    type: 'switch',
  },
  {
    path: 'intent.mode',
    section: 'GENERAL',
    label: 'AI_AGENT.INTENT.FIELDS.MODE',
    type: 'select',
    options: modeOptions,
    help: 'AI_AGENT.INTENT.FIELDS.MODE_HELP',
  },
  {
    path: 'intent.min_confidence',
    section: 'GENERAL',
    label: 'AI_AGENT.INTENT.FIELDS.MIN_CONFIDENCE',
    type: 'number',
    help: 'AI_AGENT.INTENT.FIELDS.MIN_CONFIDENCE_HELP',
  },
  {
    path: 'intent.transfer_on_distress',
    section: 'ESCALATION',
    label: 'AI_AGENT.INTENT.FIELDS.TRANSFER_ON_DISTRESS',
    type: 'number',
  },
  {
    path: 'intent.transfer_on_wants_human',
    section: 'ESCALATION',
    label: 'AI_AGENT.INTENT.FIELDS.TRANSFER_ON_WANTS_HUMAN',
    type: 'number',
  },
  {
    path: 'intent.anger_streak_threshold',
    section: 'ESCALATION',
    label: 'AI_AGENT.INTENT.FIELDS.ANGER_STREAK_THRESHOLD',
    type: 'number',
  },
  {
    path: 'intent.topic_ttl_seconds',
    section: 'UNDERSTANDING',
    label: 'AI_AGENT.INTENT.FIELDS.TOPIC_TTL_SECONDS',
    type: 'number',
  },
  {
    path: 'intent.slang_map_enabled',
    section: 'UNDERSTANDING',
    label: 'AI_AGENT.INTENT.FIELDS.SLANG_MAP_ENABLED',
    type: 'switch',
  },
  {
    path: 'intent.typo_pass_enabled',
    section: 'UNDERSTANDING',
    label: 'AI_AGENT.INTENT.FIELDS.TYPO_PASS_ENABLED',
    type: 'switch',
  },
  {
    path: 'intent.timeout_ms',
    section: 'PERFORMANCE',
    label: 'AI_AGENT.INTENT.FIELDS.TIMEOUT_MS',
    type: 'number',
  },
  {
    path: 'intent.cache_ttl_seconds',
    section: 'PERFORMANCE',
    label: 'AI_AGENT.INTENT.FIELDS.CACHE_TTL_SECONDS',
    type: 'number',
  },
];
</script>

<template>
  <ConfigForm :bot="bot" :fields="fields" :sections="sections">
    <StudioSection
      :title="$t('AI_AGENT.INTENT.SECTIONS.INTENT_SET')"
      :description="$t('AI_AGENT.INTENT.SECTIONS.INTENT_SET_HELP')"
      icon="i-lucide-tags"
    >
      <IntentSetPicker :bot="bot" />
    </StudioSection>
  </ConfigForm>
</template>
